import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { API_CONFIG } from '../../config/apiConfig';

interface DXFViewerProps {
  // For dialog mode
  open?: boolean;
  onClose?: () => void;
  fileId?: string;
  fileName?: string;

  // Common props
  projectId?: string;

  // For embedded mode (from HVACCalculationPanel)
  embedded?: boolean;
}

const DXFViewer: React.FC<DXFViewerProps> = ({
  open = true,
  onClose = () => {},
  fileId = '',
  fileName = 'CAD File',
  projectId,
  embedded = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dxfContent, setDxfContent] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [availableCadFiles, setAvailableCadFiles] = useState<any[]>([]);
  const [selectedCadFileId, setSelectedCadFileId] = useState<string>(fileId);
  const [currentFileName, setCurrentFileName] = useState<string>(fileName);

  const loadAvailableCadFiles = useCallback(async () => {
    if (!projectId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = API_CONFIG.MEP_SERVICE_URL;
      const url = `${apiUrl}/api/v1/mep/projects/${projectId}/cad-files`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const files = result.data || [];
        const filesWithDxf = files.filter((f: any) => f.hasDxf);
        setAvailableCadFiles(filesWithDxf);

        // If the initial fileId isn't in the list or no fileId, select the first available
        if (filesWithDxf.length > 0) {
          const hasSelectedFile = filesWithDxf.some((f: any) => f.id === fileId);
          if (!hasSelectedFile) {
            setSelectedCadFileId(filesWithDxf[0].id);
            setCurrentFileName(filesWithDxf[0].filename);
          } else {
            // Find and set the current file name
            const currentFile = filesWithDxf.find((f: any) => f.id === fileId);
            if (currentFile) {
              setCurrentFileName(currentFile.filename);
            }
          }
        }
      } else {
        console.error('Failed to load CAD files:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading available CAD files:', error);
    }
  }, [projectId, fileId]);

  const handleCADFileChange = (event: SelectChangeEvent) => {
    const newFileId = event.target.value;
    setSelectedCadFileId(newFileId);

    // Update the current filename when selection changes
    const selectedFile = availableCadFiles.find(f => f.id === newFileId);
    if (selectedFile) {
      setCurrentFileName(selectedFile.filename);
    }
  };

  const renderBasicDXF = useCallback((ctx: CanvasRenderingContext2D, content: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const lines = content.split('\n');
    let currentEntity: string | null = null;
    let currentLayer: string = '';
    let x1: number | null = null;
    let y1: number | null = null;
    let x2: number | null = null;
    let y2: number | null = null;
    let endAngle: number | null = null; // For ARC end angle
    let lineCount = 0;

    // Arrays to store categorized entities
    const allPoints: { x: number, y: number }[] = [];
    const wallEntities: any[] = [];
    const doorEntities: any[] = [];
    const windowEntities: any[] = [];

    ctx.lineWidth = 1 / zoom;

    // Helper function to classify entity based on layer name
    const classifyEntity = (layerName: string) => {
      const layer = layerName.toLowerCase();

      // Door patterns
      if (layer.includes('door') || layer.includes('dr') ||
          layer.includes('opening') || layer.includes('entrance')) {
        return 'door';
      }

      // Window patterns
      if (layer.includes('window') || layer.includes('win') ||
          layer.includes('glazing') || layer.includes('glass')) {
        return 'window';
      }

      // Wall patterns (default for most architectural lines)
      if (layer.includes('wall') || layer.includes('partition') ||
          layer.includes('arch') || layer.includes('plan') ||
          layer === '0' || layer === '' || layer.includes('outline')) {
        return 'wall';
      }

      // Default to wall for unrecognized patterns
      return 'wall';
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Detect LINE or ARC entity
      if (line === 'LINE' || line === 'ARC') {
        currentEntity = line;
        currentLayer = ''; // Reset layer for new entity
        x1 = y1 = x2 = y2 = null;
        endAngle = null; // Reset end angle for new entity
        continue;
      }

      // Parse layer information for both LINE and ARC
      if ((currentEntity === 'LINE' || currentEntity === 'ARC') && line === '8') {
        const nextLine = lines[i + 1]?.trim();
        if (nextLine) {
          currentLayer = nextLine;
          i++;
        }
        continue;
      }

      // Parse coordinates for LINE and ARC entities
      if (currentEntity === 'LINE' || currentEntity === 'ARC') {
        const nextLine = lines[i + 1]?.trim();

        if (line === '10' && nextLine) { // X1 (center X for ARC)
          x1 = parseFloat(nextLine);
          i++;
        } else if (line === '20' && nextLine) { // Y1 (center Y for ARC)
          y1 = parseFloat(nextLine);
          i++;
        } else if (line === '11' && nextLine && currentEntity === 'LINE') { // X2 (LINE only)
          x2 = parseFloat(nextLine);
          i++;
        } else if (line === '21' && nextLine && currentEntity === 'LINE') { // Y2 (LINE only)
          y2 = parseFloat(nextLine);
          i++;
        } else if (line === '40' && nextLine && currentEntity === 'ARC') { // Radius (ARC only)
          x2 = parseFloat(nextLine); // Store radius in x2 for ARC
          i++;
        } else if (line === '50' && nextLine && currentEntity === 'ARC') { // Start angle (ARC only)
          y2 = parseFloat(nextLine); // Store start angle in y2 for ARC
          i++;
        } else if (line === '51' && nextLine && currentEntity === 'ARC') { // End angle (ARC only)
          endAngle = parseFloat(nextLine);
          i++;
        }

        // Store entity if we have required coordinates
        const isCompleteEntity = currentEntity === 'LINE'
          ? (x1 !== null && y1 !== null && x2 !== null && y2 !== null)
          : (x1 !== null && y1 !== null && x2 !== null); // ARC needs center + radius

        if (isCompleteEntity) {
          lineCount++;

          // For bounding box calculation with null checks
          if (currentEntity === 'LINE' && x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
            allPoints.push({ x: x1, y: y1 }, { x: x2, y: y2 });
          } else if (currentEntity === 'ARC' && x1 !== null && y1 !== null && x2 !== null) {
            // For ARC, add points at the extents (center ± radius)
            const radius = x2;
            allPoints.push(
              { x: x1 - radius, y: y1 - radius },
              { x: x1 + radius, y: y1 + radius }
            );
          }

          // Classify and store in appropriate array
          const entityType = classifyEntity(currentLayer);
          const entity = currentEntity === 'LINE'
            ? { type: 'LINE', x1, y1, x2, y2 }
            : {
                type: 'ARC',
                centerX: x1,
                centerY: y1,
                radius: x2,
                startAngle: y2 || 0,
                endAngle: endAngle !== null ? endAngle : (y2 !== null ? y2 + 90 : 90) // Default to 90-degree arc if no end angle
              };


          switch (entityType) {
            case 'door':
              doorEntities.push(entity);
              break;
            case 'window':
              windowEntities.push(entity);
              break;
            default:
              wallEntities.push(entity);
              break;
          }

          currentEntity = null;
        }
      }

      // Reset entity if we hit a new section
      if (line === 'ENDSEC') {
        currentEntity = null;
        currentLayer = '';
      }
    }

    // If no lines were found, show a message
    if (lineCount === 0) {
      ctx.fillStyle = '#333';
      ctx.font = '16px Arial';
      ctx.fillText('DXF content loaded', -100, 0);
      ctx.font = '12px Arial';
      ctx.fillText('Basic rendering - lines and entities', -120, 20);
      ctx.fillText(`File has ${lines.length} lines of DXF data`, -120, 40);
      return;
    }

    // Calculate bounds
    const minX = Math.min(...allPoints.map(p => p.x));
    const maxX = Math.max(...allPoints.map(p => p.x));
    const minY = Math.min(...allPoints.map(p => p.y));
    const maxY = Math.max(...allPoints.map(p => p.y));

    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // Calculate scale to fit in canvas (leave some margin)
    const canvasWidth = isFullscreen ? window.innerWidth - 100 : 600;  // Available drawing area
    const canvasHeight = isFullscreen ? window.innerHeight - 200 : 400;
    const scaleX = canvasWidth / width;
    const scaleY = canvasHeight / height;
    const scale = Math.min(scaleX, scaleY) * 0.8; // 80% to leave margin

    // Helper function to draw entities with specific color
    const drawEntities = (entities: any[], color: string) => {
      ctx.strokeStyle = color;
      entities.forEach(entity => {
        if (entity.type === 'LINE') {
          // Draw line
          const x1Scaled = (entity.x1 - centerX) * scale;
          const y1Scaled = -(entity.y1 - centerY) * scale; // Flip Y
          const x2Scaled = (entity.x2 - centerX) * scale;
          const y2Scaled = -(entity.y2 - centerY) * scale; // Flip Y

          ctx.beginPath();
          ctx.moveTo(x1Scaled, y1Scaled);
          ctx.lineTo(x2Scaled, y2Scaled);
          ctx.stroke();
        } else if (entity.type === 'ARC') {
          // Draw arc (door swings)
          const centerXScaled = (entity.centerX - centerX) * scale;
          const centerYScaled = -(entity.centerY - centerY) * scale; // Flip Y
          const radiusScaled = entity.radius * scale;

          // Convert angles from degrees to radians
          // Since we flip Y coordinate, we need to adjust angles accordingly
          // DXF: counterclockwise from +X axis
          // Canvas with flipped Y: need to negate angles to maintain proper orientation
          const startAngleRad = -(entity.startAngle || 0) * Math.PI / 180;
          const endAngleRad = -entity.endAngle * Math.PI / 180;


          ctx.beginPath();
          // Canvas arc(x, y, radius, startAngle, endAngle, counterclockwise)
          // With Y-flipped coordinate system, we draw clockwise to match DXF orientation
          ctx.arc(centerXScaled, centerYScaled, radiusScaled, startAngleRad, endAngleRad, true);
          ctx.stroke();
        }
      });
    };

    // Draw walls in black
    drawEntities(wallEntities, '#000000');

    // Draw doors in red
    drawEntities(doorEntities, '#FF0000');

    // Draw windows in blue
    drawEntities(windowEntities, '#0000FF');

    // Show info about the drawing (adjust position based on canvas size)
    ctx.fillStyle = '#666';
    ctx.font = '12px Arial';
    const infoX = -(canvas.width / 2 - 100);
    const infoY = -(canvas.height / 2 - 30);

    // Display counts by category
    ctx.fillText(`Walls: ${wallEntities.length} (black)`, infoX, infoY);
    ctx.fillText(`Doors: ${doorEntities.length} (red)`, infoX, infoY + 15);
    ctx.fillText(`Windows: ${windowEntities.length} (blue)`, infoX, infoY + 30);
    ctx.fillText(`Total: ${lineCount} lines`, infoX, infoY + 45);

    if (isFullscreen) {
      ctx.fillText('Press ESC to exit fullscreen', infoX, infoY + 65);
    }
  }, [isFullscreen, zoom]);

  const parseAndRenderDXF = useCallback((content: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size based on fullscreen state
    if (isFullscreen) {
      canvas.width = window.innerWidth - 40; // Leave some margin
      canvas.height = window.innerHeight - 120; // Leave space for controls
    } else {
      canvas.width = 800;
      canvas.height = 600;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Apply transformations
    ctx.save();
    ctx.translate(canvas.width / 2 + panX, canvas.height / 2 + panY);
    ctx.scale(zoom, zoom);

    // Basic DXF parsing and rendering
    try {
      renderBasicDXF(ctx, content);
    } catch (renderError) {
      console.error('Error rendering DXF:', renderError);
      ctx.restore();

      // Show simple text representation
      ctx.fillStyle = '#333';
      ctx.font = '14px monospace';
      ctx.fillText('DXF file loaded successfully', 20, 30);
      ctx.fillText('Advanced rendering coming soon...', 20, 50);
      ctx.fillText(`File: ${fileName}`, 20, 70);
      return;
    }

    ctx.restore();
  }, [isFullscreen, zoom, panX, panY, fileName, renderBasicDXF]);

  const loadDXFFile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API_CONFIG.MEP_SERVICE_URL;
      const url = `${apiUrl}/api/v1/mep/cad/dxf/${selectedCadFileId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('DXF file not available. File may not have been processed yet.');
        }
        throw new Error('Failed to load DXF file');
      }

      const content = await response.text();
      setDxfContent(content);
      parseAndRenderDXF(content);
    } catch (error: any) {
      console.error('Error loading DXF file:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCadFileId, parseAndRenderDXF]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 10));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleCenter = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
    // Re-render at default zoom
    if (dxfContent) {
      parseAndRenderDXF(dxfContent);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    // Re-render with new canvas size after fullscreen toggle
    setTimeout(() => {
      if (dxfContent) {
        parseAndRenderDXF(dxfContent);
      }
    }, 100);
  };

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && isFullscreen) {
      setIsFullscreen(false);
      setTimeout(() => {
        if (dxfContent) {
          parseAndRenderDXF(dxfContent);
        }
      }, 100);
    }
  }, [isFullscreen, dxfContent, parseAndRenderDXF]);

  // Load available CAD files for the project
  useEffect(() => {
    if (open && projectId) {
      loadAvailableCadFiles();
    }
  }, [open, projectId, loadAvailableCadFiles]);

  useEffect(() => {
    if (open && selectedCadFileId) {
      loadDXFFile();
    }
  }, [open, selectedCadFileId, loadDXFFile]);

  // Add keyboard event listener for escape key
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullscreen, handleKeyPress]);

  const handleDownload = () => {
    if (dxfContent && currentFileName) {
      const blob = new Blob([dxfContent], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      // Use the current file's name, ensure it ends with .dxf
      let downloadName = currentFileName;
      if (!downloadName.toLowerCase().endsWith('.dxf')) {
        // If original file is .dwg, change extension to .dxf
        downloadName = downloadName.replace(/\.[^.]*$/, '.dxf');
      }

      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Re-render when zoom or pan changes
  useEffect(() => {
    if (dxfContent) {
      parseAndRenderDXF(dxfContent);
    }
  }, [zoom, panX, panY, dxfContent, parseAndRenderDXF]);

  // If embedded mode, render without dialog
  if (embedded || !onClose) {
    return (
      <div style={{
        height: isFullscreen ? '100vh' : '100%',
        width: isFullscreen ? '100vw' : '100%',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        backgroundColor: isFullscreen ? 'white' : 'transparent',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Typography variant="h6">
              DXF Viewer - {currentFileName}
            </Typography>
            {availableCadFiles.length > 0 && (
              <FormControl size="small" style={{ minWidth: 200 }}>
                <InputLabel id="cad-file-select-label">CAD File</InputLabel>
                <Select
                  labelId="cad-file-select-label"
                  id="cad-file-select"
                  value={selectedCadFileId}
                  label="CAD File"
                  onChange={handleCADFileChange}
                  disabled={loading}
                >
                  {availableCadFiles.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.filename}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={handleZoomIn}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={handleZoomOut}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Center & Reset">
              <IconButton size="small" onClick={handleCenter}>
                <CenterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download DXF">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
              <IconButton size="small" onClick={handleToggleFullscreen}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div style={{ flex: 1, position: 'relative' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography style={{ marginLeft: 16 }}>Loading DXF file...</Typography>
            </div>
          )}

          {error && (
            <div style={{ padding: 16 }}>
              <Alert severity="error">{error}</Alert>
            </div>
          )}

          {/* Floating controls for fullscreen mode */}
          {isFullscreen && !loading && !error && (
            <div style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: 2,
              padding: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              gap: 8,
              backdropFilter: 'blur(8px)'
            }}>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  style={{ color: 'white' }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  style={{ color: 'white' }}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Center & Reset Zoom">
                <IconButton
                  size="small"
                  onClick={handleCenter}
                  style={{ color: 'white' }}
                >
                  <CenterIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download DXF">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  style={{ color: 'white' }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exit Fullscreen">
                <IconButton
                  size="small"
                  onClick={handleToggleFullscreen}
                  style={{ color: 'white' }}
                >
                  <FullscreenExitIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}

          {!loading && !error && (
            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
              <canvas
                ref={canvasRef}
                style={{
                  border: '1px solid #ccc',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  cursor: 'move'
                }}
                onMouseDown={(e) => {
                  setIsDragging(true);
                  setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
                }}
                onMouseMove={(e) => {
                  if (isDragging) {
                    setPanX(e.clientX - dragStart.x);
                    setPanY(e.clientY - dragStart.y);
                  }
                }}
                onMouseUp={() => {
                  setIsDragging(false);
                }}
                onMouseLeave={() => {
                  setIsDragging(false);
                }}
              />
            </div>
          )}
        </div>

        {!loading && !error && !isFullscreen && (
          <div style={{ padding: 8, borderTop: '1px solid #eee', display: 'flex', gap: 16 }}>
            <Typography variant="caption">
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
            <Typography variant="caption">
              Pan: ({Math.round(panX)}, {Math.round(panY)})
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
              Click and drag to pan • Use buttons to zoom
            </Typography>
          </div>
        )}

        {/* Status bar for fullscreen embedded mode */}
        {isFullscreen && !loading && !error && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: 2,
            padding: '8px 16px',
            display: 'flex',
            gap: 24,
            fontSize: '0.875rem',
            backdropFilter: 'blur(8px)'
          }}>
            <Typography variant="caption" color="inherit">
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
            <Typography variant="caption" color="inherit">
              Pan: ({Math.round(panX)}, {Math.round(panY)})
            </Typography>
            <Typography variant="caption" color="inherit">
              Fullscreen Mode • Press ESC or click ⛷ to exit
            </Typography>
          </div>
        )}
      </div>
    );
  }

  // Dialog mode (original)
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isFullscreen ? false : "lg"}
      fullWidth={!isFullscreen}
      fullScreen={isFullscreen}
      PaperProps={{
        style: {
          height: isFullscreen ? '100vh' : '80vh',
          width: isFullscreen ? '100vw' : 'auto',
          maxWidth: isFullscreen ? 'none' : undefined,
          margin: isFullscreen ? 0 : undefined
        }
      }}
    >
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Typography variant="h6">
            DXF Viewer - {currentFileName}
          </Typography>
          {availableCadFiles.length > 0 && (
            <FormControl size="small" style={{ minWidth: 200 }}>
              <InputLabel id="cad-file-select-label">CAD File</InputLabel>
              <Select
                labelId="cad-file-select-label"
                id="cad-file-select"
                value={selectedCadFileId}
                label="CAD File"
                onChange={handleCADFileChange}
                disabled={loading}
              >
                {availableCadFiles.map((file) => (
                  <MenuItem key={file.id} value={file.id}>
                    {file.filename}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Zoom In">
            <IconButton size="small" onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton size="small" onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Center & Reset">
            <IconButton size="small" onClick={handleCenter}>
              <CenterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download DXF">
            <IconButton size="small" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
            <IconButton size="small" onClick={handleToggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>

      <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
            <Typography style={{ marginLeft: 16 }}>Loading DXF file...</Typography>
          </div>
        )}

        {error && (
          <div style={{ padding: 16 }}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        {/* Floating controls for fullscreen dialog mode */}
        {isFullscreen && !loading && !error && (
          <div style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: 2,
            padding: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            gap: 8,
            backdropFilter: 'blur(8px)'
          }}>
            <IconButton
              size="small"
              onClick={handleZoomIn}
              title="Zoom In"
              style={{ color: 'white' }}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleZoomOut}
              title="Zoom Out"
              style={{ color: 'white' }}
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCenter}
              title="Center & Reset Zoom"
              style={{ color: 'white' }}
            >
              <CenterIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDownload}
              title="Download DXF"
              style={{ color: 'white' }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleToggleFullscreen}
              title="Exit Fullscreen"
              style={{ color: 'white' }}
            >
              <FullscreenExitIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              title="Close Viewer"
              style={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}

        {!loading && !error && (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
            <canvas
              ref={canvasRef}
              style={{
                border: '1px solid #ccc',
                maxWidth: '100%',
                maxHeight: '100%',
                cursor: 'move'
              }}
              onMouseDown={(e) => {
                setIsDragging(true);
                setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
              }}
              onMouseMove={(e) => {
                if (isDragging) {
                  setPanX(e.clientX - dragStart.x);
                  setPanY(e.clientY - dragStart.y);
                }
              }}
              onMouseUp={() => {
                setIsDragging(false);
              }}
              onMouseLeave={() => {
                setIsDragging(false);
              }}
            />
          </div>
        )}

        {!loading && !error && !isFullscreen && (
          <div style={{ padding: 8, borderTop: '1px solid #eee', display: 'flex', gap: 16 }}>
            <Typography variant="caption">
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
            <Typography variant="caption">
              Pan: ({Math.round(panX)}, {Math.round(panY)})
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
              Click and drag to pan • Use buttons to zoom
            </Typography>
          </div>
        )}

        {/* Status bar for fullscreen dialog mode */}
        {isFullscreen && !loading && !error && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: 2,
            padding: '8px 16px',
            display: 'flex',
            gap: 24,
            fontSize: '0.875rem',
            backdropFilter: 'blur(8px)'
          }}>
            <Typography variant="caption" color="inherit">
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
            <Typography variant="caption" color="inherit">
              Pan: ({Math.round(panX)}, {Math.round(panY)})
            </Typography>
            <Typography variant="caption" color="inherit">
              Fullscreen Mode • Press ESC or click ⛷ to exit
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DXFViewer;
